import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    useSelector,
} from 'react-redux';

import {
    Box,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';

import {
    GridColumnVisibilityModel,
    GridFilterModel,
    GridPaginationModel,
    GridPreferencePanelsValue,
    useGridApiRef,
} from '@mui/x-data-grid-pro';

import Iconify from 'src/components/iconify';

import GenericDataGrid from 'src/components/data-grid/generic-data-grid';

import {
    useCurrentWorkspace,
} from 'src/context/reducers/app-settings';

import {
    RootState,
} from 'src/context/store';

import {
    useGetConversionsKpisQuery,
} from 'src/context/api/conversions/api';

import {
    brandedCampaignsVisibility,
} from 'src/utils/branded-campaigns';

// ----------------------------------------------------------------------

type ConversionDimension =
    | 'country'
    | 'device'
    | 'date'
    | 'platform';

// ----------------------------------------------------------------------

const TABS: {
    label: string;
    value: ConversionDimension;
}[] = [
        {
            label: 'Countries',
            value: 'country',
        },
        {
            label: 'Devices',
            value: 'device',
        },
        {
            label: 'Date',
            value: 'date',
        },
        {
            label: 'Platform',
            value: 'platform',
        },
    ];

// ----------------------------------------------------------------------

const GRID_HEIGHT = 520;

const safeNumber = (
    value: unknown
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : 0;
};

const safeDivide = (
    numerator: number,
    denominator: number
) =>
    denominator
        ? numerator / denominator
        : 0;

const CONVERSION_KPI_OVERRIDES = {
    blended_conversions: {
        id: 'blended_conversions',
        en: 'Blended Conversions',
        fr: 'Blended Conversions',
        viz: 'base',
        format: 'float:2',
        show: true,
        order: 100,
        invert: false,
        color: '#18A055',
    },
    paid_conversions: {
        id: 'paid_conversions',
        en: 'Paid Conversions',
        fr: 'Paid Conversions',
        viz: 'base',
        format: 'float:2',
        show: false,
        order: 101,
        invert: false,
        color: '#FFB700',
    },
    organic_conversions: {
        id: 'organic_conversions',
        en: 'Organic Conversions',
        fr: 'Organic Conversions',
        viz: 'base',
        format: 'float:2',
        show: false,
        order: 102,
        invert: false,
        color: '#18A055',
    },
    blended_cost_per_conv: {
        id: 'blended_cost_per_conv',
        en: 'Cost per blended conv.',
        fr: 'Cost per blended conv.',
        viz: 'base',
        format: 'currency:2',
        show: true,
        order: 103,
        invert: true,
    },
    paid_cost_per_conv: {
        id: 'paid_cost_per_conv',
        en: 'Paid Cost per Conv.',
        fr: 'Paid Cost per Conv.',
        viz: 'base',
        format: 'currency:2',
        show: false,
        order: 104,
        invert: true,
    },
    blended_cvr: {
        id: 'blended_cvr',
        en: 'Blended CVR',
        fr: 'Blended CVR',
        viz: 'base',
        format: 'percentage:2',
        show: true,
        order: 105,
        invert: false,
    },
    organic_cvr: {
        id: 'organic_cvr',
        en: 'Organic CVR',
        fr: 'Organic CVR',
        viz: 'base',
        format: 'percentage:2',
        show: false,
        order: 106,
        invert: false,
    },
    paid_cvr: {
        id: 'paid_cvr',
        en: 'Paid CVR',
        fr: 'Paid CVR',
        viz: 'base',
        format: 'percentage:2',
        show: false,
        order: 107,
        invert: false,
    },
    blended_revenue: {
        id: 'blended_revenue',
        en: 'Blended Revenue',
        fr: 'Blended Revenue',
        viz: 'base',
        format: 'currency:2',
        show: true,
        order: 108,
        invert: false,
        color: '#18A055',
    },
    paid_revenue: {
        id: 'paid_revenue',
        en: 'Paid Revenue',
        fr: 'Paid Revenue',
        viz: 'base',
        format: 'currency:2',
        show: false,
        order: 109,
        invert: false,
        color: '#FFB700',
    },
    organic_revenue: {
        id: 'organic_revenue',
        en: 'Organic Revenue',
        fr: 'Organic Revenue',
        viz: 'base',
        format: 'currency:2',
        show: false,
        order: 110,
        invert: false,
        color: '#18A055',
    },
    roas: {
        id: 'roas',
        en: 'ROAS',
        fr: 'ROAS',
        viz: 'base',
        format: 'float:2',
        show: true,
        order: 111,
        invert: false,
    },
    blended_aov: {
        id: 'blended_aov',
        en: 'AOV',
        fr: 'AOV',
        viz: 'base',
        format: 'currency:2',
        show: true,
        order: 112,
        invert: false,
    },
    search_paid_conversions: {
        id: 'search_paid_conversions',
        en: 'Search Paid Conversions',
        fr: 'Search Paid Conversions',
        viz: 'base',
        format: 'float:2',
        show: false,
        order: 113,
        invert: false,
        color: '#FFB700',
    },
    shopping_paid_conversions: {
        id: 'shopping_paid_conversions',
        en: 'Shopping Paid Conversions',
        fr: 'Shopping Paid Conversions',
        viz: 'base',
        format: 'float:2',
        show: false,
        order: 114,
        invert: false,
        color: '#FF8C00',
    },
    pmax_paid_conversions: {
        id: 'pmax_paid_conversions',
        en: 'PMax Conversions',
        fr: 'PMax Conversions',
        viz: 'base',
        format: 'float:2',
        show: false,
        order: 115,
        invert: false,
        color: '#FFE299',
    },
    search_paid_revenue: {
        id: 'search_paid_revenue',
        en: 'Search Paid Revenue',
        fr: 'Search Paid Revenue',
        viz: 'base',
        format: 'currency:2',
        show: false,
        order: 116,
        invert: false,
        color: '#FFB700',
    },
    shopping_paid_revenue: {
        id: 'shopping_paid_revenue',
        en: 'Shopping Paid Revenue',
        fr: 'Shopping Paid Revenue',
        viz: 'base',
        format: 'currency:2',
        show: false,
        order: 117,
        invert: false,
        color: '#FF8C00',
    },
    pmax_paid_revenue: {
        id: 'pmax_paid_revenue',
        en: 'PMax Revenue',
        fr: 'PMax Revenue',
        viz: 'base',
        format: 'currency:2',
        show: false,
        order: 118,
        invert: false,
        color: '#FFE299',
    },
};

// ----------------------------------------------------------------------

export default function ConversionsGrid() {
    const workspace: any =
        useCurrentWorkspace();

    const filterStore =
        useSelector(
            (state: RootState) =>
                state.filters
        );

    // ----------------------------------------------------------------------
    // Workspace model
    // ----------------------------------------------------------------------

    const isLead =
        (
            workspace?.template ??
            ''
        )
            .toLowerCase()
            .includes('lead');

    const isCustom =
        Boolean(
            workspace?.custom
        );

    const {
        showShopping,
        showPmax,
    } =
        brandedCampaignsVisibility(
            workspace
        );

    // ----------------------------------------------------------------------
    // Selected dimension
    // ----------------------------------------------------------------------

    const [
        dimension,
        setDimension,
    ]: any =
        useState<ConversionDimension>(
            'country'
        );
    const [
        showEvolution,
        setShowEvolution,
    ] =
        useState(true);

    // ----------------------------------------------------------------------
    // Date comparison label
    // ----------------------------------------------------------------------

    const isLastYearComparison =
        useMemo(
            () => {
                if (
                    !filterStore?.compare ||
                    !filterStore?.start ||
                    !filterStore?.end ||
                    !filterStore?.compareStart ||
                    !filterStore?.compareEnd
                ) {
                    return false;
                }

                const start =
                    new Date(
                        filterStore.start
                    );

                const end =
                    new Date(
                        filterStore.end
                    );

                const compareStart =
                    new Date(
                        filterStore.compareStart
                    );

                const compareEnd =
                    new Date(
                        filterStore.compareEnd
                    );

                if (
                    Number.isNaN(
                        start.getTime()
                    ) ||
                    Number.isNaN(
                        end.getTime()
                    ) ||
                    Number.isNaN(
                        compareStart.getTime()
                    ) ||
                    Number.isNaN(
                        compareEnd.getTime()
                    )
                ) {
                    return false;
                }

                const isSameUtcDate =
                    (
                        left: Date,
                        right: Date
                    ) =>
                        left.getUTCFullYear() ===
                        right.getUTCFullYear() &&
                        left.getUTCMonth() ===
                        right.getUTCMonth() &&
                        left.getUTCDate() ===
                        right.getUTCDate();

                const shiftUtcDays =
                    (
                        date: Date,
                        days: number
                    ) => {
                        const shifted =
                            new Date(
                                Date.UTC(
                                    date.getUTCFullYear(),
                                    date.getUTCMonth(),
                                    date.getUTCDate()
                                )
                            );

                        shifted.setUTCDate(
                            shifted.getUTCDate() +
                            days
                        );

                        return shifted;
                    };

                if (
                    filterStore
                        ?.timestep
                        ?.toLowerCase() ===
                    'year_week'
                ) {
                    const normalizedStart =
                        new Date(
                            Date.UTC(
                                start.getUTCFullYear(),
                                start.getUTCMonth(),
                                start.getUTCDate()
                            )
                        );

                    const startDay =
                        normalizedStart
                            .getUTCDay();

                    const daysUntilMonday =
                        startDay === 1
                            ? 0
                            : startDay === 0
                                ? 1
                                : 8 - startDay;

                    const firstMonday =
                        shiftUtcDays(
                            normalizedStart,
                            daysUntilMonday
                        );

                    const expectedCompareStart =
                        shiftUtcDays(
                            firstMonday,
                            -364
                        );

                    const expectedCompareEnd =
                        shiftUtcDays(
                            end,
                            -364
                        );

                    return (
                        isSameUtcDate(
                            compareStart,
                            expectedCompareStart
                        ) &&
                        isSameUtcDate(
                            compareEnd,
                            expectedCompareEnd
                        )
                    );
                }

                const isPreviousYearDate =
                    (
                        current: Date,
                        compared: Date
                    ) =>
                        current.getFullYear() -
                        compared.getFullYear() ===
                        1 &&
                        current.getMonth() ===
                        compared.getMonth() &&
                        current.getDate() ===
                        compared.getDate();

                return (
                    isPreviousYearDate(
                        start,
                        compareStart
                    ) &&
                    isPreviousYearDate(
                        end,
                        compareEnd
                    )
                );
            },
            [
                filterStore?.compare,
                filterStore?.start,
                filterStore?.end,
                filterStore?.compareStart,
                filterStore?.compareEnd,
                filterStore?.timestep,
            ]
        );

    const dateComparisonLabel =
        useMemo(
            () => {
                if (
                    !filterStore?.compare
                ) {
                    return '';
                }

                const timestep =
                    filterStore
                        ?.timestep
                        ?.toLowerCase();

                if (
                    isLastYearComparison
                ) {
                    switch (
                    timestep
                    ) {
                        case 'date':
                        case 'date_day':
                            return 'vs same day previous year';

                        case 'year_week':
                            return 'vs same week previous year';

                        case 'year_month':
                            return 'vs same month previous year';

                        default:
                            return 'vs previous year';
                    }
                }

                switch (
                timestep
                ) {
                    case 'date':
                    case 'date_day':
                        return 'vs previous day';

                    case 'year_week':
                        return 'vs previous week';

                    case 'year_month':
                        return 'vs previous month';

                    default:
                        return 'vs previous period';
                }
            },
            [
                filterStore?.compare,
                filterStore?.timestep,
                isLastYearComparison,
            ]
        );

    // ----------------------------------------------------------------------
    // Grid refs
    // ----------------------------------------------------------------------

    const gridApiRef =
        useGridApiRef();

    const columnsBtnRef =
        useRef<HTMLButtonElement>(
            null
        );

    const filterBtnRef =
        useRef<HTMLButtonElement>(
            null
        );

    const columnsWasOpenRef =
        useRef(false);

    const filterWasOpenRef =
        useRef(false);

    // ----------------------------------------------------------------------
    // Grid states
    // ----------------------------------------------------------------------

    const [
        panelAnchor,
        setPanelAnchor,
    ] =
        useState<HTMLElement | null>(
            null
        );

    const [
        filters,
        setFilterModel,
    ] =
        useState<GridFilterModel>({
            items: [],
        });

    const [
        visibilityModel,
        setVisibilityModel,
    ] =
        useState<GridColumnVisibilityModel>(
            {}
        );

    const [
        paginationModel,
        setPaginationModel,
    ] =
        useState<GridPaginationModel>({
            page: 0,
            pageSize: 10,
        });

    // ----------------------------------------------------------------------
    // API
    // ----------------------------------------------------------------------

    const {
        data,
        currentData,
        error,
        isFetching,
    } =
        useGetConversionsKpisQuery(
            {
                workspaceId:
                    workspace?.id,

                // ----------------------------------------------------------
                // Temporary fixed dates
                // ----------------------------------------------------------
                startDate:
                    filterStore?.start?.split('T')[0],

                endDate:
                    filterStore?.end?.split('T')[0],

                compareStartDate:
                    filterStore?.compareStart?.split('T')[0],

                compareEndDate:
                    filterStore?.compareEnd?.split('T')[0],

                // ----------------------------------------------------------
                // Filters
                // ----------------------------------------------------------

                countries:
                    filterStore
                        ?.countries ?? [],

                devices:
                    filterStore
                        ?.devices ?? [],

                platforms:
                    filterStore
                        ?.platforms ?? [],

                // ----------------------------------------------------------
                // Selected dimension
                // ----------------------------------------------------------

                dimension,

                // ----------------------------------------------------------
                // Date granularity
                // ----------------------------------------------------------

                timestep:
                    filterStore
                        ?.timestep ??
                    'year_week',
            },
            {
                skip:
                    !workspace?.id ||
                    !filterStore?.start ||
                    !filterStore?.end,
            }
        );

    // `data` can keep the previous query result while RTK Query fetches
    // another dimension. `currentData` only belongs to the CURRENT args.
    //
    // Country filters are a special case: RTK Query can immediately expose
    // cached `currentData` for a country selection, so `!currentData` alone
    // would not restart the skeleton. Keep the last settled country signature
    // and hide the grid until the request for the new selection has settled.
    const countryFilterSignature =
        JSON.stringify(
            filterStore?.countries ??
            []
        );

    const [
        settledCountryFilterSignature,
        setSettledCountryFilterSignature,
    ] =
        useState(
            countryFilterSignature
        );

    useEffect(() => {
        if (
            !isFetching &&
            settledCountryFilterSignature !==
            countryFilterSignature
        ) {
            setSettledCountryFilterSignature(
                countryFilterSignature
            );
        }
    }, [
        countryFilterSignature,
        isFetching,
        settledCountryFilterSignature,
    ]);

    const isCountryFilterLoading =
        countryFilterSignature !==
        settledCountryFilterSignature;

    const showGridSkeleton =
        (
            !currentData &&
            !error
        ) ||
        isCountryFilterLoading;

    // ----------------------------------------------------------------------
    // Dimension field
    // ----------------------------------------------------------------------

    const dimensionField =
        useMemo(
            () => {
                switch (
                dimension
                ) {
                    case 'country':
                        return 'country';

                    case 'device':
                        return 'device';

                    case 'platform':
                        return 'platform';

                    case 'date':
                    default: {
                        const timestep =
                            filterStore
                                ?.timestep;

                        if (
                            timestep ===
                            'year_month'
                        ) {
                            return 'year_month';
                        }

                        if (
                            timestep ===
                            'date' ||
                            timestep ===
                            'date_day'
                        ) {
                            return 'date';
                        }

                        return 'year_week';
                    }
                }
            },
            [
                dimension,
                filterStore?.timestep,
            ]
        );

    // ----------------------------------------------------------------------
    // Metric mapper
    //
    // Tous les modèles gardent les métriques générales.
    //
    // STANDARD :
    // Organic
    // Search Paid
    // Shopping Paid si disponible
    // PMax si disponible
    //
    // CUSTOM :
    // Organic
    // Paid
    //
    // Les champs Search / Shopping / PMax ne sont donc pas injectés
    // dans les rows Custom.
    // ----------------------------------------------------------------------

    const buildMetricValues =
        (
            row: any
        ) => {
            const adCost =
                safeNumber(
                    row?.ad_cost
                );

            const adClicks =
                safeNumber(
                    row?.ad_clicks
                );

            const organicClicks =
                safeNumber(
                    row?.organic_clicks
                );

            const brandTrafficSplit = {
                organic:
                    organicClicks,

                pmax:
                    safeNumber(
                        row?.ad_clicks_from_pmax
                    ),

                paidSearch:
                    safeNumber(
                        row?.ad_clicks_from_search
                    ),

                paidShopping:
                    safeNumber(
                        row?.ad_clicks_from_shopping
                    ),
            };

            const brandTraffic =
                adClicks +
                organicClicks;

            const blendedConversions =
                safeNumber(
                    row?.blended_conversions
                );

            const paidConversions =
                safeNumber(
                    row?.paid_conversions
                );

            const organicConversions =
                safeNumber(
                    row?.organic_conversions
                );

            const blendedRevenue =
                safeNumber(
                    row?.blended_revenue
                );

            const paidRevenue =
                safeNumber(
                    row?.paid_revenue
                );

            const organicRevenue =
                safeNumber(
                    row?.organic_revenue
                );

            // ----------------------------------------------------------
            // Metrics visible in every model
            // ----------------------------------------------------------

            const baseMetrics = {
                ad_cost:
                    adCost,

                blended_conversions:
                    blendedConversions,

                blended_cost_per_conv:
                    safeNumber(
                        row
                            ?.blended_cost_per_conv
                    ),

                blended_cvr:
                    safeNumber(
                        row?.blended_cvr
                    ),
            };

            // ----------------------------------------------------------
            // LEAD
            // ----------------------------------------------------------

            if (
                isLead
            ) {
                const leadBaseMetrics = {
                    ...baseMetrics,

                    total_brand_traffic:
                        brandTraffic,

                    brand_traffic_split:
                        brandTrafficSplit,

                    blended_cpc:
                        safeDivide(
                            adCost,
                            brandTraffic
                        ),

                    paid_cost_per_conv:
                        safeNumber(
                            row
                                ?.paid_cost_per_conv
                        ),

                    paid_cvr:
                        safeDivide(
                            paidConversions,
                            adClicks
                        ) * 100,

                    organic_cvr:
                        safeDivide(
                            organicConversions,
                            organicClicks
                        ) * 100,
                };

                // ------------------------------------------------------
                // CUSTOM LEAD
                //
                // Custom = agrégats Paid / Organic.
                // ------------------------------------------------------

                if (
                    isCustom
                ) {
                    return {
                        ...leadBaseMetrics,

                        paid_conversions:
                            paidConversions,

                        organic_conversions:
                            organicConversions,
                    };
                }

                // ------------------------------------------------------
                // STANDARD LEAD
                //
                // Exact Figma behavior:
                //
                // Visible by default:
                // Dimension
                // Ad Cost
                // Brand Traffic
                // Blended CPC
                // Blended Conversions
                // Cost per blended conv.
                // Blended CVR
                //
                // Hidden / available in Show-Hide:
                // Organic Conv.
                // Paid Search Conv.
                // Paid Shopping Conv. (if enabled)
                // Paid PMax Conv. (if enabled)
                // Paid Cost per Conv.
                // Paid CVR
                // Organic CVR
                //
                // No aggregate "Paid Conversions" column in Standard Lead.
                // ------------------------------------------------------

                return {
                    ...leadBaseMetrics,

                    organic_conversions:
                        organicConversions,

                    search_paid_conversions:
                        safeNumber(
                            row
                                ?.search_paid_conversions
                        ),

                    ...(showShopping
                        ? {
                            shopping_paid_conversions:
                                safeNumber(
                                    row
                                        ?.shopping_paid_conversions
                                ),
                        }
                        : {}),

                    ...(showPmax
                        ? {
                            pmax_paid_conversions:
                                safeNumber(
                                    row
                                        ?.pmax_paid_conversions
                                ),
                        }
                        : {}),
                };
            }

            // ----------------------------------------------------------
            // E-COMMERCE CUSTOM
            // ----------------------------------------------------------

            if (
                isCustom
            ) {
                return {
                    ...baseMetrics,

                    paid_conversions:
                        paidConversions,

                    organic_conversions:
                        organicConversions,

                    paid_cost_per_conv:
                        safeNumber(
                            row
                                ?.paid_cost_per_conv
                        ),

                    paid_cvr:
                        safeDivide(
                            paidConversions,
                            adClicks
                        ) * 100,

                    organic_cvr:
                        safeDivide(
                            organicConversions,
                            organicClicks
                        ) * 100,

                    blended_revenue:
                        blendedRevenue,

                    paid_revenue:
                        paidRevenue,

                    organic_revenue:
                        organicRevenue,

                    roas:
                        safeNumber(
                            row?.roas
                        ),

                    blended_aov:
                        safeNumber(
                            row?.blended_aov
                        ),
                };
            }

            // ----------------------------------------------------------
            // E-COMMERCE STANDARD
            //
            // Visible defaults are controlled by defaultVisibleFields.
            // The detailed fields below exist but start hidden.
            // ----------------------------------------------------------

            return {
                ...baseMetrics,

                organic_conversions:
                    organicConversions,

                search_paid_conversions:
                    safeNumber(
                        row
                            ?.search_paid_conversions
                    ),

                ...(showShopping
                    ? {
                        shopping_paid_conversions:
                            safeNumber(
                                row
                                    ?.shopping_paid_conversions
                            ),
                    }
                    : {}),

                ...(showPmax
                    ? {
                        pmax_paid_conversions:
                            safeNumber(
                                row
                                    ?.pmax_paid_conversions
                            ),
                    }
                    : {}),

                paid_cost_per_conv:
                    safeNumber(
                        row
                            ?.paid_cost_per_conv
                    ),

                paid_cvr:
                    safeDivide(
                        paidConversions,
                        adClicks
                    ) * 100,

                organic_cvr:
                    safeDivide(
                        organicConversions,
                        organicClicks
                    ) * 100,

                blended_revenue:
                    blendedRevenue,

                organic_revenue:
                    organicRevenue,

                search_paid_revenue:
                    safeNumber(
                        row
                            ?.search_paid_revenue
                    ),

                ...(showShopping
                    ? {
                        shopping_paid_revenue:
                            safeNumber(
                                row
                                    ?.shopping_paid_revenue
                            ),
                    }
                    : {}),

                ...(showPmax
                    ? {
                        pmax_paid_revenue:
                            safeNumber(
                                row
                                    ?.pmax_paid_revenue
                            ),
                    }
                    : {}),

                roas:
                    safeNumber(
                        row?.roas
                    ),

                blended_aov:
                    safeNumber(
                        row?.blended_aov
                    ),
            };
        };

    // ----------------------------------------------------------------------
    // Compare lookup
    //
    // IMPORTANT :
    // GenericDataGrid utilise row.compare lorsque compare=true.
    //
    // On fournit donc TOUJOURS compare + diff.
    //
    // Cela évite notamment le crash en revenant de Funnel si le store global
    // conserve temporairement compare=true.
    // ----------------------------------------------------------------------

    const compareByDimension =
        useMemo(
            () =>
                new Map(
                    (
                        data
                            ?.time_series_compare ??
                        []
                    ).map(
                        (
                            row: any
                        ) => [
                                String(
                                    row
                                        ?.dimension ??
                                    ''
                                ),
                                row,
                            ]
                    )
                ),
            [
                data
                    ?.time_series_compare,
            ]
        );

    // ----------------------------------------------------------------------
    // Rows
    // ----------------------------------------------------------------------

    const rows =
        useMemo(
            () => {
                const mappedRows =
                    (
                        data?.time_series ??
                        []
                    ).map(
                        (
                            row: any,
                            index: number
                        ) => {
                            const dimensionValue =
                                dimension ===
                                    'platform' &&
                                    row?.dimension
                                    ? row.dimension
                                        .charAt(0)
                                        .toUpperCase() +
                                    row.dimension
                                        .slice(1)
                                        .toLowerCase()
                                    : row?.dimension;

                            const metrics =
                                buildMetricValues(
                                    row
                                );

                            const compareSource =
                                dimension ===
                                    'date'
                                    ? (
                                        data
                                            ?.time_series_compare ??
                                        []
                                    )[index]
                                    : compareByDimension
                                        .get(
                                            String(
                                                row
                                                    ?.dimension ??
                                                ''
                                            )
                                        );

                            const compare =
                                buildMetricValues(
                                    compareSource ??
                                    {}
                                );

                            const diff =
                                Object.keys(
                                    metrics
                                ).reduce(
                                    (
                                        accumulator,
                                        key
                                    ) => {
                                        const currentValue =
                                            (
                                                metrics as any
                                            )[key];

                                        const compareValue =
                                            (
                                                compare as any
                                            )[key];

                                        if (
                                            typeof currentValue ===
                                            'number' &&
                                            typeof compareValue ===
                                            'number'
                                        ) {
                                            accumulator[
                                                key
                                            ] =
                                                currentValue -
                                                compareValue;
                                        }

                                        return accumulator;
                                    },
                                    {} as Record<
                                        string,
                                        number
                                    >
                                );

                            return {
                                id:
                                    `${dimension}-${row?.dimension}-${index}`,

                                [dimensionField]:
                                    dimensionValue,

                                ...metrics,

                                compare,
                                diff,
                            };
                        }
                    );

                if (
                    dimension !==
                    'date'
                ) {
                    return mappedRows;
                }

                // Date table: newest bucket first.
                // The chart keeps the API's ascending chronological order.
                return [
                    ...mappedRows,
                ].sort(
                    (
                        left: any,
                        right: any
                    ) =>
                        String(
                            right[
                            dimensionField
                            ] ??
                            ''
                        ).localeCompare(
                            String(
                                left[
                                dimensionField
                                ] ??
                                ''
                            )
                        )
                );
            },
            [
                data?.time_series,
                data?.time_series_compare,
                dimension,
                dimensionField,
                compareByDimension,
                isLead,
                isCustom,
                showShopping,
                showPmax,
            ]
        );

    // ----------------------------------------------------------------------
    // Pagination locale
    // ----------------------------------------------------------------------

    const paginatedRows =
        useMemo(
            () => {
                const start =
                    paginationModel.page *
                    paginationModel.pageSize;

                const end =
                    start +
                    paginationModel.pageSize;

                return rows.slice(
                    start,
                    end
                );
            },
            [
                rows,
                paginationModel.page,
                paginationModel.pageSize,
            ]
        );


    // ----------------------------------------------------------------------
    // Grid height
    // ----------------------------------------------------------------------

    const fitsWithoutCap =
        useMemo(
            () => {
                const HEADER_H = 36;
                const ROW_H = 44;
                const FOOTER_H = 40;

                const rowsCount =
                    paginatedRows.length;

                return (
                    HEADER_H +
                    rowsCount * ROW_H +
                    FOOTER_H
                ) <= GRID_HEIGHT;
            },
            [
                paginatedRows,
            ]
        );

    // ----------------------------------------------------------------------
    // Default visible columns
    //
    // FIGMA LEAD / CUSTOM LEAD :
    //
    // Dimension
    // Ad Cost
    // Brand Traffic
    // Blended CPC
    // Blended Conversions
    // Cost per blended conv.
    // Blended CVR
    //
    // FIGMA ECOM / CUSTOM ECOM :
    //
    // Dimension
    // Ad Cost
    // Blended Conversions
    // Cost per blended conv.
    // Blended CVR
    // Blended Revenue
    // ROAS
    // AOV
    // ----------------------------------------------------------------------

    const defaultVisibleFields =
        useMemo(
            () =>
                isLead
                    ? [
                        'ad_cost',
                        'total_brand_traffic',
                        'blended_cpc',
                        'blended_conversions',
                        'blended_cost_per_conv',
                        'blended_cvr',
                    ]
                    : [
                        'ad_cost',
                        'blended_conversions',
                        'blended_cost_per_conv',
                        'blended_cvr',
                        'blended_revenue',
                        'roas',
                        'blended_aov',
                    ],
            [
                isLead,
            ]
        );

    // ----------------------------------------------------------------------
    // Column order
    //
    // Les autres métriques restent accessibles via Show/hide columns.
    // ----------------------------------------------------------------------

    const columnOrder =
        useMemo(
            () => {
                if (
                    isLead
                ) {
                    if (
                        isCustom
                    ) {
                        // Lead Custom — exact order of the full-table Figma.
                        //
                        // Visible by default:
                        // Dimension
                        // Ad Cost
                        // Brand Traffic
                        // Blended CPC
                        // Blended Conversions
                        // Cost per blended conv.
                        // Blended CVR
                        //
                        // Hidden / available in Show-Hide:
                        // Organic Conv.
                        // Paid Conv.
                        // Paid Cost per Conv.
                        // Paid CVR
                        // Organic CVR
                        return [
                            dimensionField,
                            'ad_cost',
                            'total_brand_traffic',
                            'blended_cpc',
                            'blended_conversions',

                            'organic_conversions',
                            'paid_conversions',

                            'blended_cost_per_conv',
                            'paid_cost_per_conv',
                            'blended_cvr',
                            'paid_cvr',
                            'organic_cvr',
                        ];
                    }

                    // Lead Standard — exact order of the full-table Figma.
                    return [
                        dimensionField,
                        'ad_cost',
                        'total_brand_traffic',
                        'blended_cpc',
                        'blended_conversions',

                        'organic_conversions',
                        'search_paid_conversions',

                        ...(showShopping
                            ? [
                                'shopping_paid_conversions',
                            ]
                            : []),

                        ...(showPmax
                            ? [
                                'pmax_paid_conversions',
                            ]
                            : []),

                        'blended_cost_per_conv',
                        'paid_cost_per_conv',
                        'blended_cvr',
                        'paid_cvr',
                        'organic_cvr',
                    ];
                }

                if (
                    isCustom
                ) {
                    // E-commerce Custom — exact order of the full-table Figma.
                    //
                    // Visible by default:
                    // Dimension
                    // Ad Cost
                    // Blended Conversions
                    // Cost per blended conv.
                    // Blended CVR
                    // Blended Revenue
                    // ROAS
                    // AOV
                    //
                    // Hidden / available in Show-Hide:
                    // Organic Conv.
                    // Paid Conv.
                    // Paid Cost per Conv.
                    // Paid CVR
                    // Organic CVR
                    // Organic Revenue
                    // Paid Revenue
                    return [
                        dimensionField,
                        'ad_cost',
                        'blended_conversions',

                        'organic_conversions',
                        'paid_conversions',

                        'blended_cost_per_conv',
                        'paid_cost_per_conv',
                        'blended_cvr',
                        'paid_cvr',
                        'organic_cvr',

                        'blended_revenue',
                        'organic_revenue',
                        'paid_revenue',

                        'roas',
                        'blended_aov',
                    ];
                }

                // E-commerce Standard — same order as the full-table Figma.
                return [
                    dimensionField,
                    'ad_cost',
                    'blended_conversions',

                    'organic_conversions',
                    'search_paid_conversions',

                    ...(showShopping
                        ? [
                            'shopping_paid_conversions',
                        ]
                        : []),

                    ...(showPmax
                        ? [
                            'pmax_paid_conversions',
                        ]
                        : []),

                    'blended_cost_per_conv',
                    'paid_cost_per_conv',
                    'blended_cvr',
                    'paid_cvr',
                    'organic_cvr',
                    'blended_revenue',
                    'organic_revenue',
                    'search_paid_revenue',

                    ...(showShopping
                        ? [
                            'shopping_paid_revenue',
                        ]
                        : []),

                    ...(showPmax
                        ? [
                            'pmax_paid_revenue',
                        ]
                        : []),

                    'roas',
                    'blended_aov',
                ];
            },
            [
                dimensionField,
                isLead,
                isCustom,
                showShopping,
                showPmax,
            ]
        );

    // ----------------------------------------------------------------------
    // Labels
    // ----------------------------------------------------------------------

    const columnLabelOverrides =
        useMemo(
            () => ({
                platform: 'Platform',

                total_brand_traffic:
                    'Brand Traffic',

                blended_cpc:
                    'Blended CPC',

                blended_conversions:
                    'Blended Conversions',

                blended_cost_per_conv:
                    'Cost per blended conv.',

                blended_cvr:
                    'Blended CVR',

                blended_revenue:
                    'Blended Revenue',

                roas: 'Blended ROAS',
                blended_aov: 'Blended AOV',

                paid_conversions:
                    'Paid Conversions',

                organic_conversions:
                    'Organic Conversions',

                paid_revenue:
                    'Paid Revenue',

                organic_revenue:
                    'Organic Revenue',

                paid_cost_per_conv:
                    'Paid Cost per Conv.',

                organic_cvr:
                    'Organic CVR',

                paid_cvr:
                    'Paid CVR',

                search_paid_conversions:
                    'Search Paid Conversions',

                shopping_paid_conversions:
                    'Shopping Paid Conversions',

                pmax_paid_conversions:
                    'PMax Conversions',

                search_paid_revenue:
                    'Search Paid Revenue',

                shopping_paid_revenue:
                    'Shopping Paid Revenue',

                pmax_paid_revenue:
                    'PMax Revenue',
            }),
            []
        );

    // ----------------------------------------------------------------------
    // Metric bars
    //
    // On garde uniquement des magnitude bars ici.
    //
    // Important :
    // le renderer "composition" actuel du GenericDataGrid est dédié au
    // Brand Traffic et utilise des champs de trafic.
    //
    // L'utiliser directement sur Blended Conversions / Revenue afficherait
    // une composition fausse.
    // ----------------------------------------------------------------------

    const columnMinWidths =
        useMemo(
            () => ({
                blended_cost_per_conv:
                    260,
                paid_cost_per_conv:
                    260,
            }),
            []
        );

    const metricBarTypes =
        useMemo(
            () => ({
                ad_cost:
                    'magnitude' as const,

                total_brand_traffic:
                    'composition' as const,

                blended_cpc:
                    'none' as const,

                blended_conversions:
                    'blended-composition' as const,

                blended_cost_per_conv:
                    'none' as const,

                blended_cvr:
                    'none' as const,

                blended_revenue:
                    'blended-composition' as const,

                roas:
                    'none' as const,

                blended_aov:
                    'none' as const,

                paid_conversions:
                    'magnitude' as const,

                organic_conversions:
                    'magnitude' as const,

                paid_revenue:
                    'magnitude' as const,

                organic_revenue:
                    'magnitude' as const,

                paid_cost_per_conv:
                    'none' as const,

                organic_cvr:
                    'none' as const,

                paid_cvr:
                    'none' as const,

                search_paid_conversions:
                    'magnitude' as const,

                shopping_paid_conversions:
                    'magnitude' as const,

                pmax_paid_conversions:
                    'magnitude' as const,

                search_paid_revenue:
                    'magnitude' as const,

                shopping_paid_revenue:
                    'magnitude' as const,

                pmax_paid_revenue:
                    'magnitude' as const,
            }),
            []
        );

    const metricBarColors =
        useMemo(
            () => ({
                ad_cost:
                    '#DAA335',

                total_brand_traffic:
                    '#18A055',

                blended_conversions:
                    '#18A055',

                blended_revenue:
                    '#18A055',

                organic_conversions:
                    '#18A055',

                organic_revenue:
                    '#18A055',

                paid_conversions:
                    '#FFB700',

                paid_revenue:
                    '#FFB700',

                search_paid_conversions:
                    '#FFB700',

                search_paid_revenue:
                    '#FFB700',

                shopping_paid_conversions:
                    '#FF8C00',

                shopping_paid_revenue:
                    '#FF8C00',

                pmax_paid_conversions:
                    '#FFE299',

                pmax_paid_revenue:
                    '#FFE299',
            }),
            []
        );

    // ----------------------------------------------------------------------
    // Change tab
    // ----------------------------------------------------------------------

    const handleChangeTab = (
        _: React.SyntheticEvent,
        newValue: ConversionDimension
    ) => {
        if (
            newValue ===
            dimension
        ) {
            return;
        }

        setDimension(
            newValue
        );

        setPaginationModel(
            (
                previous
            ) => ({
                ...previous,
                page: 0,
            })
        );

        setFilterModel({
            items: [],
        });

        gridApiRef
            .current
            ?.hidePreferences?.();
    };

    // ----------------------------------------------------------------------
    // Columns panel
    // ----------------------------------------------------------------------

    const handleColumnsMouseDown =
        () => {
            const panel =
                gridApiRef
                    .current
                    ?.state
                    ?.preferencePanel;

            columnsWasOpenRef.current =
                !!(
                    panel?.open &&
                    panel
                        ?.openedPanelValue ===
                    GridPreferencePanelsValue
                        .columns
                );
        };

    const handleColumnsClick =
        () => {
            if (
                columnsWasOpenRef.current
            ) {
                gridApiRef
                    .current
                    ?.hidePreferences?.();

                return;
            }

            setPanelAnchor(
                columnsBtnRef.current
            );

            gridApiRef
                .current
                ?.showPreferences?.(
                    GridPreferencePanelsValue
                        .columns
                );
        };

    // ----------------------------------------------------------------------
    // Filter panel
    // ----------------------------------------------------------------------

    const handleFilterMouseDown =
        () => {
            const panel =
                gridApiRef
                    .current
                    ?.state
                    ?.preferencePanel;

            filterWasOpenRef.current =
                !!(
                    panel?.open &&
                    panel
                        ?.openedPanelValue ===
                    GridPreferencePanelsValue
                        .filters
                );
        };

    const handleFilterClick =
        () => {
            if (
                filterWasOpenRef.current
            ) {
                gridApiRef
                    .current
                    ?.hidePreferences?.();

                return;
            }

            setPanelAnchor(
                filterBtnRef.current
            );

            gridApiRef
                .current
                ?.showFilterPanel?.();
        };

    // ----------------------------------------------------------------------
    // Export
    // ----------------------------------------------------------------------

    const handleExport =
        () => {
            gridApiRef
                .current
                ?.exportDataAsCsv?.();
        };

    // ----------------------------------------------------------------------
    // Render
    // ----------------------------------------------------------------------

    return (
        <Paper
            sx={{
                mt: 2,

                backgroundColor:
                    '#FFFFFF',

                border:
                    '1px solid #EAEAEA',

                borderRadius:
                    '16px',

                boxShadow:
                    '0px 0px 2px 0px rgba(145, 158, 171, 0.20), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',

                overflow:
                    'hidden',

                // ----------------------------------------------------------
                // Grid
                // ----------------------------------------------------------

                '& .MuiDataGrid-root':
                {
                    border: 0,

                    '--DataGrid-containerBackground':
                        '#FAFBFB',

                    '--DataGrid-pinnedBackground':
                        '#FAFBFB',
                },

                '& .MuiDataGrid-columnHeaders':
                {
                    backgroundColor:
                        '#FAFBFB',
                },

                // ----------------------------------------------------------
                // Column headers
                // ----------------------------------------------------------


                '& .MuiDataGrid-columnHeader':
                {
                    borderRight:
                        '1px solid rgba(145, 158, 171, 0.20)',
                },

                '& .MuiDataGrid-columnHeaderTitle':
                {
                    color:
                        '#637381',

                    fontSize:
                        '14px',

                    fontWeight:
                        600,
                },

                // ----------------------------------------------------------
                // Horizontal padding
                // ----------------------------------------------------------

                '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell':
                {
                    paddingLeft:
                        '20px',

                    paddingRight:
                        '20px',
                },


                // ----------------------------------------------------------
                // Cells
                // ----------------------------------------------------------

                '& .MuiDataGrid-cell':
                {
                    color:
                        '#333C47',

                    fontSize:
                        '14px',

                    borderBottom:
                        '1px solid rgba(145, 158, 171, 0.20)',

                    borderRight:
                        '1px solid rgba(145, 158, 171, 0.20)',
                },

                // ----------------------------------------------------------
                // Row hover
                // ----------------------------------------------------------

                '& .MuiDataGrid-row:hover':
                {
                    backgroundColor:
                        'rgba(145, 158, 171, 0.08)',
                },

                '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft':
                {
                    backgroundColor:
                        'rgba(145, 158, 171, 0.08) !important',
                },

                // ----------------------------------------------------------
                // Footer
                // ----------------------------------------------------------

                '& .MuiDataGrid-footerContainer':
                {
                    minHeight:
                        '40px',

                    height:
                        '40px',

                    backgroundColor:
                        '#FFFFFF',

                    borderTop:
                        '1px solid rgba(145, 158, 171, 0.20)',

                    justifyContent:
                        'flex-end',

                    '& .MuiTablePagination-root':
                    {
                        color:
                            '#919EAB',

                        fontSize:
                            '14px',

                        width:
                            '100%',
                    },

                    '& .MuiTablePagination-toolbar':
                    {
                        minHeight:
                            '40px !important',

                        height:
                            '40px',

                        paddingLeft:
                            '20px',

                        paddingRight:
                            '8px',

                        display:
                            'flex',

                        alignItems:
                            'center',

                        justifyContent:
                            'flex-end',
                    },

                    '& .MuiTablePagination-selectLabel':
                    {
                        color:
                            '#919EAB',

                        fontSize:
                            '14px',

                        margin:
                            0,

                        marginRight:
                            '4px',
                    },

                    '& .MuiTablePagination-input':
                    {
                        marginLeft:
                            0,

                        marginRight:
                            '30px',
                    },

                    '& .MuiTablePagination-select':
                    {
                        color:
                            '#919EAB',

                        fontSize:
                            '14px',
                    },

                    '& .MuiTablePagination-displayedRows':
                    {
                        color:
                            '#919EAB',

                        fontSize:
                            '14px',

                        margin:
                            0,

                        marginRight:
                            '8px',
                    },

                    '& .MuiTablePagination-actions':
                    {
                        display:
                            'flex',

                        alignItems:
                            'center',

                        gap:
                            '8px',

                        marginLeft:
                            0,

                        '& .MuiIconButton-root':
                        {
                            width:
                                '34px',

                            height:
                                '34px',

                            padding:
                                '8px',

                            color:
                                '#919EAB',
                        },
                    },
                },
            }}
        >
            {/* ========================================================== */}
            {/* HEADER */}
            {/* ========================================================== */}

            <Box
                sx={{
                    height:
                        48,
                    pl:
                        '10px',

                    pr:
                        '20px',

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'space-between',

                    borderBottom:
                        '1px solid #EAEAEA',
                }}
            >
                {/* ------------------------------------------------------ */}
                {/* TABS */}
                {/* ------------------------------------------------------ */}

                <Tabs
                    value={
                        dimension
                    }

                    onChange={
                        handleChangeTab
                    }

                    TabIndicatorProps={{
                        sx: {
                            height:
                                '2px',

                            backgroundColor:
                                '#7635DC',
                        },
                    }}

                    sx={{
                        minHeight:
                            48,

                        '& .MuiTabs-flexContainer':
                        {
                            height:
                                '100%',
                        },

                        '& .MuiTab-root':
                        {
                            minHeight:
                                48,

                            minWidth:
                                'auto',

                            padding:
                                0,

                            textTransform:
                                'none',

                            fontFamily:
                                'Public Sans',

                            fontSize:
                                '14px',

                            fontWeight:
                                600,

                            lineHeight:
                                '17.5px',

                            color:
                                '#919EAB',

                            '&:not(:last-of-type)':
                            {
                                marginRight:
                                    '24px',
                            },
                        },

                        '& .Mui-selected':
                        {
                            color:
                                '#7635DC !important',
                        },
                    }}
                >
                    {TABS
                        .filter(
                            (tab) =>
                                tab.value !== 'platform' ||
                                (filterStore?.loadedPlatform?.length ?? 0) >= 2
                        )
                        .map(
                            (tab) => (
                                <Tab
                                    key={tab.value}
                                    value={tab.value}
                                    label={
                                        tab.value === 'date'
                                            ? (
                                                <Stack
                                                    component="span"
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                >
                                                    <Box component="span">
                                                        {tab.label}
                                                    </Box>

                                                    {dateComparisonLabel && (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                px: '8px',
                                                                height: '22px',
                                                                borderRadius: '11px',
                                                                backgroundColor:
                                                                    'rgba(139, 61, 255, 0.10)',
                                                                color: '#8B3DFF',
                                                                fontSize: '12px',
                                                                fontWeight: 500,
                                                                lineHeight: '18px',
                                                                whiteSpace: 'nowrap',
                                                                pointerEvents: 'none',
                                                            }}
                                                        >
                                                            {dateComparisonLabel}
                                                        </Box>
                                                    )}
                                                </Stack>
                                            )
                                            : tab.label
                                    }
                                />
                            )
                        )}
                </Tabs>

                {/* ------------------------------------------------------ */}
                {/* ACTIONS */}
                {/* ------------------------------------------------------ */}

                <Stack
                    direction="row"

                    alignItems="center"

                    spacing={1}
                >
                    {/* -------------------------------------------------- */}
                    {/* SHOW / HIDE COLUMNS */}
                    {filterStore?.compare && (
                        <Tooltip
                            title={
                                showEvolution
                                    ? 'Showing % evolution — click for absolute difference'
                                    : 'Showing absolute difference — click for % evolution'
                            }
                            placement="top"
                        >
                            <Box
                                onClick={() =>
                                    setShowEvolution(
                                        (value) =>
                                            !value
                                    )
                                }
                                sx={{
                                    display:
                                        'flex',
                                    alignItems:
                                        'center',
                                    gap:
                                        '2px',
                                    px:
                                        '6px',
                                    py:
                                        '4px',
                                    borderRadius:
                                        '6px',
                                    cursor:
                                        'pointer',
                                    userSelect:
                                        'none',
                                    backgroundColor:
                                        'rgba(145, 158, 171, 0.08)',
                                    color:
                                        '#1E88E5',
                                }}
                            >
                                <Iconify
                                    icon={
                                        showEvolution
                                            ? 'material-symbols:percent-rounded'
                                            : 'material-symbols:tag-rounded'
                                    }
                                    width={
                                        16
                                    }
                                    sx={{
                                        color:
                                            'inherit',
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize:
                                            14,
                                        fontWeight:
                                            500,
                                        lineHeight:
                                            '16.8px',
                                        color:
                                            'inherit',
                                        whiteSpace:
                                            'nowrap',
                                        mx:
                                            '2px',
                                    }}
                                >
                                    evolution
                                </Typography>

                                <Iconify
                                    icon="mi:switch"
                                    width={
                                        16
                                    }
                                    sx={{
                                        color:
                                            'inherit',
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    )}

                    {/* -------------------------------------------------- */}

                    <Tooltip
                        title="Show/hide columns"

                        placement="top"
                    >
                        <IconButton
                            ref={
                                columnsBtnRef
                            }

                            size="small"

                            onMouseDown={
                                handleColumnsMouseDown
                            }

                            onClick={
                                handleColumnsClick
                            }

                            sx={{
                                width:
                                    26,

                                height:
                                    26,

                                minWidth:
                                    26,

                                padding:
                                    '4px',

                                display:
                                    'flex',

                                justifyContent:
                                    'center',

                                alignItems:
                                    'center',

                                borderRadius:
                                    '6px',

                                color:
                                    '#919EAB',

                                '&:hover':
                                {
                                    backgroundColor:
                                        'rgba(145, 158, 171, 0.08)',
                                },
                            }}
                        >
                            <Iconify
                                icon="mingcute:calendar-week-line"

                                width={
                                    18
                                }
                            />
                        </IconButton>
                    </Tooltip>

                    {/* -------------------------------------------------- */}
                    {/* FILTER */}
                    {/* -------------------------------------------------- */}

                    <Tooltip
                        title="Filters"

                        placement="top"
                    >
                        <IconButton
                            ref={
                                filterBtnRef
                            }

                            size="small"

                            onMouseDown={
                                handleFilterMouseDown
                            }

                            onClick={
                                handleFilterClick
                            }

                            sx={{
                                width:
                                    26,

                                height:
                                    26,

                                minWidth:
                                    26,

                                padding:
                                    '4px',

                                display:
                                    'flex',

                                justifyContent:
                                    'center',

                                alignItems:
                                    'center',

                                borderRadius:
                                    '6px',

                                color:
                                    '#919EAB',

                                '&:hover':
                                {
                                    backgroundColor:
                                        'rgba(145, 158, 171, 0.08)',
                                },
                            }}
                        >
                            <Iconify
                                icon="ic:round-filter-list"

                                width={
                                    18
                                }
                            />
                        </IconButton>
                    </Tooltip>

                    {/* -------------------------------------------------- */}
                    {/* EXPORT */}
                    {/* -------------------------------------------------- */}

                    <Tooltip
                        title="Export"

                        placement="top"
                    >
                        <IconButton
                            size="small"

                            onClick={
                                handleExport
                            }

                            sx={{
                                width:
                                    26,

                                height:
                                    26,

                                minWidth:
                                    26,

                                padding:
                                    '4px',

                                display:
                                    'flex',

                                justifyContent:
                                    'center',

                                alignItems:
                                    'center',

                                borderRadius:
                                    '6px',

                                color:
                                    '#919EAB',

                                '&:hover':
                                {
                                    backgroundColor:
                                        'rgba(145, 158, 171, 0.08)',
                                },
                            }}
                        >
                            <Iconify
                                icon="lets-icons:export"
                                width={
                                    18
                                }
                            />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* ========================================================== */}
            {/* BODY */}
            {/* ========================================================== */}

            <Box>
                {showGridSkeleton ? (
                    <Skeleton
                        width="100%"
                        height={164}
                        variant="rectangular"
                        animation="wave"
                        sx={{
                            borderRadius: 0,
                            transform: 'none',
                        }}
                    />
                ) : (
                    <GenericDataGrid
                        apiRef={
                            gridApiRef
                        }

                        // --------------------------------------------------
                        // Data
                        // --------------------------------------------------

                        data={
                            rows
                        }

                        loading={
                            false
                        }

                        hideToolbar

                        // Conversions loads the complete row set locally,
                        // so sorting must be handled by DataGrid on the client.
                        sortingMode="client"

                        // --------------------------------------------------
                        // Dimension
                        // --------------------------------------------------

                        pinnedColumns={[
                            dimensionField,
                        ]}

                        showDimensionFlags={
                            dimension ===
                            'country'
                        }

                        // --------------------------------------------------
                        // Fields / model
                        // --------------------------------------------------

                        defaultVisibleFields={
                            defaultVisibleFields
                        }

                        columnOrder={
                            columnOrder
                        }

                        columnLabelOverrides={
                            columnLabelOverrides
                        }


                        columnMinWidths={
                            columnMinWidths
                        }
                        kpiOverrides={
                            CONVERSION_KPI_OVERRIDES
                        }

                        // --------------------------------------------------
                        // Bars
                        // --------------------------------------------------

                        metricBarTypes={
                            metricBarTypes
                        }

                        metricBarColors={
                            metricBarColors
                        }

                        brandTrafficMode="organic-pmax-search-shopping"

                        // --------------------------------------------------
                        // Layout
                        // --------------------------------------------------

                        columnHeaderHeight={
                            36
                        }

                        rowHeight={
                            44
                        }

                        gridHeight={
                            fitsWithoutCap
                                ? undefined
                                : GRID_HEIGHT
                        }

                        leftAlign

                        // --------------------------------------------------
                        // Pagination
                        //
                        // On fournit toutes les rows au DataGrid et on laisse
                        // sa pagination client gérer la page affichée.
                        // Ainsi l'export CSV contient toutes les lignes,
                        // et pas seulement la page courante.
                        // --------------------------------------------------

                        paginationModel={
                            paginationModel
                        }

                        onPaginationModelChange={
                            setPaginationModel
                        }

                        // --------------------------------------------------
                        // Filters
                        // --------------------------------------------------

                        filterMode="client"

                        filterModel={
                            filters
                        }

                        onFilterModelChange={
                            setFilterModel
                        }

                        // --------------------------------------------------
                        // Visibility
                        // --------------------------------------------------

                        visibilityModel={
                            visibilityModel
                        }

                        inlineCompare

                        evolutionMode={
                            showEvolution
                        }

                        persistColumnVisibility

                        onVisibilityModelChange={
                            setVisibilityModel
                        }

                        // --------------------------------------------------
                        // External panels
                        // --------------------------------------------------

                        panelAnchorEl={
                            panelAnchor
                        }
                    />
                )}
            </Box>
        </Paper>
    );
}